import { makeAutoObservable, toJS } from 'mobx'
import { clampNumber } from 'shared/lib/number'
import {
  ChatbotDisqualifyBehaviorStatus,
  ChatbotFollowUpAssertiveness,
  type IChatbotFollowUpAssertiveness,
  type IChatbotSettings,
} from './type'

export class ChatbotSettings {
  static DefaultAttempts = 10
  static MaxAttempts = 20
  static MinAttempts = 1
  static DefaultFollowUpWindow = 3
  static DefaultAutoFollowUpEnabled = true
  static DefaultFollowUpAssertiveness = ChatbotFollowUpAssertiveness.Steady

  knowledgeBaseUsage = false
  maxAttempts = ChatbotSettings.DefaultAttempts
  instructions = ''
  private _followUpWindow = ChatbotSettings.DefaultFollowUpWindow
  private _autoFollowUpEnabled = ChatbotSettings.DefaultAutoFollowUpEnabled
  private _followUpAssertiveness: IChatbotFollowUpAssertiveness =
    ChatbotSettings.DefaultFollowUpAssertiveness
  private _skipKnownFields = true
  private _currentStep = ''
  private _allowReEntering = false
  private _originalValues: Partial<IChatbotSettings> = {}
  private _data_collection_instruction = ''
  private _qualifyEnabled = false
  private _disqualifyBehavior: ChatbotDisqualifyBehaviorStatus =
    ChatbotDisqualifyBehaviorStatus.Stop
  private _qualifyInstruction = ''
  sessionLifetime = 1

  constructor(item?: IChatbotSettings) {
    this.syncOrigin(item)

    makeAutoObservable(this)
  }

  get dataCollectionInstruction() {
    return this._data_collection_instruction
  }

  setDataCollectionInstruction = (value: string) => {
    this._data_collection_instruction = value
  }

  get disqualifyBehavior() {
    return this._disqualifyBehavior
  }

  setDisqualifyBehavior = (value: ChatbotDisqualifyBehaviorStatus) => {
    this._disqualifyBehavior = value
  }

  get qualifyInstruction() {
    return this._qualifyInstruction
  }

  setQualifyInstruction = (value: string, removeError?: () => void) => {
    this._qualifyInstruction = value
    removeError?.()
  }

  onAutoFollowUpToggle = (value: boolean) => {
    this._autoFollowUpEnabled = value
  }

  get autoFollowUpEnabled() {
    return this._autoFollowUpEnabled
  }

  get followUpWindow() {
    return this._followUpWindow
  }

  get followUpAssertiveness() {
    return this._followUpAssertiveness
  }

  get skipKnownFields() {
    return this._skipKnownFields
  }

  get currentStep() {
    return this._currentStep
  }

  get allowReEntering() {
    return this._allowReEntering
  }

  get originalValues() {
    return this._originalValues
  }

  get qualifyEnabled() {
    return this._qualifyEnabled
  }

  setKnowledgeBaseUsage = (isEnabled: boolean) => {
    this.knowledgeBaseUsage = isEnabled
  }

  setQualifyEnabled(isEnabled: boolean) {
    this._qualifyEnabled = isEnabled
  }

  setMaxAttempts(maxAttempts: number) {
    this.maxAttempts = clampNumber(
      maxAttempts,
      ChatbotSettings.MinAttempts,
      ChatbotSettings.MaxAttempts
    )
  }

  setInstruction = (instruction: string, removeError?: () => void) => {
    this.instructions = instruction
    removeError?.()
  }

  setFollowUpWindow = (value: number) => {
    this._followUpWindow = value
  }

  setSessionLifetime = (value: number) => {
    this.sessionLifetime = value
  }

  setFollowUpAssertiveness = (value: IChatbotFollowUpAssertiveness) => {
    this._followUpAssertiveness = value
  }

  setSkipKnownFields = (value: boolean) => {
    this._skipKnownFields = value
  }

  setAllowReEntering = (value: boolean) => {
    this._allowReEntering = value
  }

  setOriginalValue = <K extends keyof IChatbotSettings>(key: K, value: string) => {
    this._originalValues[key] = { value } as IChatbotSettings[K]
  }

  resetInstructions = (removeError: () => void) => {
    this.instructions = this._originalValues?.instructions?.value ?? ''
    removeError()
  }

  handoff_enabled: IChatbotSettings['handoff_enabled'] = null
  set_handoff_enabled = (handoff_enabled: typeof this.handoff_enabled) => {
    this.handoff_enabled = handoff_enabled
  }

  handoff_notify_settings: IChatbotSettings['handoff_notify_settings'] = null
  set_handoff_notify_settings = (handoff_notify_settings: typeof this.handoff_notify_settings) => {
    this.handoff_notify_settings = handoff_notify_settings
  }

  syncOrigin(item?: IChatbotSettings | null) {
    this._originalValues = item || {}

    this.maxAttempts = item?.max_attempts?.value ?? ChatbotSettings.DefaultAttempts
    this.knowledgeBaseUsage = item?.use_knowledge_base?.value ?? false
    this.instructions = item?.instructions?.value ?? ''
    this._followUpWindow = item?.followup_window?.value ?? ChatbotSettings.DefaultFollowUpWindow
    this._autoFollowUpEnabled =
      item?.enable_followup?.value ?? ChatbotSettings.DefaultAutoFollowUpEnabled
    this._followUpAssertiveness =
      item?.followup_assertiveness?.value ?? ChatbotSettings.DefaultFollowUpAssertiveness
    this._skipKnownFields = item?.skip_known_fields?.value ?? true
    this._currentStep = item?.current_step?.value ?? ''
    this._allowReEntering = item?.allow_re_entering?.value ?? false
    this._data_collection_instruction = item?.data_collection_instruction?.value ?? ''
    this._qualifyEnabled = item?.qualify_enabled?.value ?? false
    this._disqualifyBehavior =
      item?.disqualify_behavior?.value ?? ChatbotDisqualifyBehaviorStatus.Stop
    this._qualifyInstruction = item?.qualify_instruction?.value ?? ''
    this.sessionLifetime = item?.session_lifetime?.value ?? 1
    this.handoff_enabled = item?.handoff_enabled
    this.handoff_notify_settings = item?.handoff_notify_settings
  }

  toJSON(): IChatbotSettings {
    const instructions = this.instructions
      ? {
          value: this.instructions,
        }
      : null

    return toJS({
      ...this._originalValues,
      max_attempts: { value: this.maxAttempts },
      use_knowledge_base: { value: this.knowledgeBaseUsage },
      qualify_enabled: { value: this._qualifyEnabled },
      disqualify_behavior: { value: this._disqualifyBehavior },
      qualify_instruction: { value: this._qualifyInstruction },
      data_collection_instruction: { value: this._data_collection_instruction },
      instructions,
      followup_window: { value: this._followUpWindow },
      enable_followup: { value: this._autoFollowUpEnabled },
      followup_assertiveness: { value: this._followUpAssertiveness },
      skip_known_fields: { value: this.skipKnownFields },
      allow_re_entering: { value: this._allowReEntering },
      session_lifetime: { value: this.sessionLifetime },
      handoff_enabled: this.handoff_enabled,
      handoff_notify_settings: this.handoff_notify_settings,
    })
  }

  reset() {
    this.maxAttempts = ChatbotSettings.DefaultAttempts
    this._followUpWindow = ChatbotSettings.DefaultFollowUpWindow
    this._autoFollowUpEnabled = ChatbotSettings.DefaultAutoFollowUpEnabled
    this._followUpAssertiveness = ChatbotSettings.DefaultFollowUpAssertiveness
    this._qualifyEnabled = false
    this._disqualifyBehavior = ChatbotDisqualifyBehaviorStatus.Stop
    this._qualifyInstruction = ''
    this.sessionLifetime = 1
    this.handoff_enabled = null
    this.handoff_notify_settings = null
  }
}
